import styled from "styled-components";
import {
  Check,
  Plus,
  Minus,
  Trash2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";

import { theme } from "../theme";
import { useT } from "../i18n";

// The item list inside the payment dialog.
//
// Two rules are structural here rather than cosmetic, because both were real defects:
//
//   1. A per-room charge CANNOT render outside a room group. Extra bed, early check-in and late
//      check-out have no representation anywhere else, and every non-canceled room gets a group even
//      when it has nothing chargeable — so the FO can count groups against the booking and trust the
//      list is complete. The dialog header never names a room.
//
//   2. The quantity stepper is ALWAYS visible, starting at 0. Hiding it until an item is ticked (the
//      previous design) makes it undiscoverable — a control you cannot see until you have already
//      committed is not a picker.
const Group = styled.div`
  margin-bottom: 0.9rem;
`;

const GroupHead = styled.div`
  align-items: baseline;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  padding-bottom: 0.25rem;
`;

const GroupName = styled.span`
  color: ${theme.ink};
  font-size: 0.86rem;
  font-weight: 800;
`;

const GroupSub = styled.span`
  color: #4a4f63;
  font-size: 0.76rem;
  font-weight: 700;
`;

const TransitChip = styled.span`
  background: #f5f3ff;
  border-radius: 999px;
  color: #6d28d9;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.05rem 0.4rem;
`;

const Row = styled.div`
  align-items: center;
  background: ${(props) => (props.$on ? "#eef3fb" : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? "#cddcf7" : theme.border)};
  border-radius: 9px;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.32rem;
  opacity: ${(props) => (props.$dim ? 0.6 : 1)};
  padding: 0.45rem 0.6rem;
`;

const Main = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Name = styled.div`
  color: ${theme.ink};
  font-size: 0.86rem;
  font-weight: 700;
`;

// Dark ink: this line carries the price basis, the caps and the reasons.
const Sub = styled.div`
  color: #4a4f63;
  font-size: 0.74rem;
  margin-top: 0.05rem;
`;

const Warn = styled.div`
  align-items: center;
  color: #8a5a00;
  display: flex;
  font-size: 0.74rem;
  font-weight: 700;
  gap: 0.22rem;
  margin-top: 0.08rem;
`;

const Amount = styled.span`
  color: ${theme.ink};
  font-size: 0.86rem;
  font-weight: 800;
  white-space: nowrap;
`;

const Tick = styled.button`
  align-items: center;
  background: ${(props) => (props.$on ? theme.gold : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 6px;
  color: ${(props) => (props.$on ? "#ffffff" : theme.ink700)};
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 24px;
  justify-content: center;
  width: 24px;
  &:disabled {
    cursor: not-allowed;
  }
`;

const Stepper = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 0.2rem;
`;

const StepBtn = styled.button`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 5px;
  cursor: pointer;
  height: 23px;
  line-height: 1;
  width: 23px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

const StepValue = styled.span`
  font-size: 0.84rem;
  font-weight: 800;
  min-width: 1.05rem;
  text-align: center;
`;

const AddBtn = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px dashed ${theme.gold};
  border-radius: 7px;
  color: ${theme.ink};
  cursor: pointer;
  display: inline-flex;
  font-size: 0.76rem;
  font-weight: 800;
  gap: 0.22rem;
  padding: 0.22rem 0.5rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const StateChip = styled.span`
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  padding: 0.05rem 0.35rem;
  ${(props) =>
    props.$paid
      ? "background:#e6f6ee;color:#1b6e56;"
      : "background:#f4f2ee;color:#5e6278;"}
`;

const HeldChip = styled.span`
  background: #e6f6ee;
  border-radius: 999px;
  color: #1b6e56;
  font-size: 0.66rem;
  font-weight: 800;
  margin-left: 0.25rem;
  padding: 0.05rem 0.35rem;
`;

const RemoveBtn = styled.button`
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 6px;
  color: #b42318;
  cursor: pointer;
  flex: 0 0 auto;
  line-height: 1;
  padding: 0.22rem 0.3rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const Empty = styled.div`
  color: #4a4f63;
  font-size: 0.78rem;
  padding: 0.25rem 0 0.4rem;
`;

const FolioEditor = (props) => {
  const { t } = useT();
  const groups = props.groups || [];

  const renderPending = (item) => {
    const on = props.isPicked(item);
    return (
      <Row key={item.key} $on={on}>
        <Tick
          type="button"
          $on={on}
          disabled={item.selectable === false || item.required}
          aria-pressed={on}
          aria-label={item.label}
          onClick={() => props.onTogglePick(item)}
        >
          {on ? <Check size={13} /> : null}
        </Tick>
        <Main>
          <Name>
            {item.label}
            {item.kind === "deposit" ? (
              <>
                {" "}
                <HeldChip>{t("pay_refundable")}</HeldChip>
              </>
            ) : null}
          </Name>
          {item.detail ? <Sub>{item.detail}</Sub> : null}
          {item.required ? <Sub>{t("pay_cannot_skip")}</Sub> : null}
          {item.selectable === false && item.reason_id ? (
            <Warn>
              <AlertTriangle size={11} />
              {item.reason_id}
            </Warn>
          ) : null}
        </Main>
        {/* A pending extra bed steps IN PLACE — never release-and-re-reserve, which would return the
            guest's beds to the pool while the change is in flight. */}
        {item.kind === "rental" ? (
          <Stepper>
            <StepBtn
              type="button"
              disabled={props.busy}
              onClick={() => props.onChangeQuantity(item, item.quantity - 1)}
            >
              <Minus size={11} />
            </StepBtn>
            <StepValue>{item.quantity}</StepValue>
            <StepBtn
              type="button"
              disabled={props.busy || item.quantity >= (item.max_quantity || 5)}
              onClick={() => props.onChangeQuantity(item, item.quantity + 1)}
            >
              <Plus size={11} />
            </StepBtn>
          </Stepper>
        ) : null}
        <Amount>{item.amount_formatted}</Amount>
        {/* Removal lives next to the thing being removed. Extra bed steps to zero instead — the
            stepper already reads as "how many", and a second control for the same change is the
            redundancy this dialog exists to delete. */}
        {item.kind === "early_checkin" || item.kind === "late_checkout" ? (
          <RemoveBtn
            type="button"
            disabled={props.busy}
            aria-label={t("addon_remove")}
            title={t("addon_remove")}
            onClick={() => props.onRemove(item)}
          >
            <Trash2 size={12} />
          </RemoveBtn>
        ) : null}
      </Row>
    );
  };

  // An add composes with the stepper and commits with a button, deliberately two gestures: an add
  // reserves real inventory housekeeping must deliver, and the reservation carries no hold expiry —
  // a one-click stepper would let a mis-click consume stock permanently.
  const renderAvailable = (item) => {
    if (item.hidden) return null;
    const draft = props.draftQty(item);
    const unavailable = item.state === "unavailable";
    const hours = Number(item.adds_hours || 0);

    return (
      <Row key={item.key} $dim={unavailable}>
        <Main>
          <Name>{item.label}</Name>
          <Sub>
            {item.unit_price_formatted
              ? `${item.unit_price_formatted}/${item.unit} · `
              : ""}
            {item.detail}
          </Sub>
          {unavailable && item.reason_id ? (
            <Warn>
              <AlertTriangle size={11} />
              {item.reason_id}
            </Warn>
          ) : null}
          {item.needs_override ? (
            <Warn>
              <AlertTriangle size={11} />
              {t("cart_override_warn")}
            </Warn>
          ) : null}
        </Main>
        {item.quantity_editable && !unavailable ? (
          <Stepper>
            <StepBtn
              type="button"
              disabled={draft <= 0}
              onClick={() => props.onDraftQty(item, draft - 1)}
            >
              <Minus size={11} />
            </StepBtn>
            <StepValue>{draft}</StepValue>
            <StepBtn
              type="button"
              disabled={draft >= (item.max_quantity || 5)}
              onClick={() => props.onDraftQty(item, draft + 1)}
            >
              <Plus size={11} />
            </StepBtn>
          </Stepper>
        ) : null}
        {unavailable ? null : (
          <AddBtn
            type="button"
            disabled={props.busy || (item.quantity_editable && draft < 1)}
            onClick={() => props.onAdd(item, draft)}
          >
            {item.kind === "late_checkout" ? (
              <RotateCcw size={11} />
            ) : (
              <Plus size={11} />
            )}
            {item.kind === "late_checkout" && hours === 1
              ? t("pay_add_hour")
              : t("pay_add")}
          </AddBtn>
        )}
      </Row>
    );
  };

  const renderLocked = (item) => (
    <Row key={item.key} $dim>
      <Main>
        <Name>
          {item.label}
          {item.quantity ? ` ×${item.quantity}` : ""}
        </Name>
        {item.detail ? <Sub>{item.detail}</Sub> : null}
      </Main>
      <StateChip $paid={item.state === "paid"}>
        {item.state === "paid" ? t("pay_settled") : t("pay_on_bill")}
      </StateChip>
      <Amount>{item.amount_formatted}</Amount>
    </Row>
  );

  const renderItem = (item) => {
    if (item.state === "pending") return renderPending(item);
    if (item.state === "available" || item.state === "unavailable")
      return renderAvailable(item);
    return renderLocked(item);
  };

  return (
    <div>
      {groups.map((group) => {
        const visible = group.items.filter((item) => !item.hidden);
        return (
          <Group key={group.key}>
            <GroupHead>
              <GroupName>{group.label}</GroupName>
              {group.sublabel ? <GroupSub>{group.sublabel}</GroupSub> : null}
              {group.transit ? (
                <TransitChip>{t("pay_transit")}</TransitChip>
              ) : null}
            </GroupHead>
            {visible.length === 0 ? (
              <Empty>{t("pay_nothing_open")}</Empty>
            ) : (
              visible.map((item) => renderItem(item))
            )}
          </Group>
        );
      })}
    </div>
  );
};

export default FolioEditor;
